/**
 * HookLibrary — W48 Marketing Dashboard Component (C)
 *
 * outreach_hooks grouped by campaign_theme with:
 *   - count badge per group
 *   - last-fired timestamp
 *   - status pill (approved / draft / rejected count)
 *
 * No Recharts. Tailwind + custom SVG icons where needed.
 * Canon palette + teal #0F766E for approved state.
 */

import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";

interface HookGroup {
  campaign_theme: string;
  count: number;
  approved: number;
  draft: number;
  rejected: number;
  last_fired: string | null;
  hooks: Array<{
    id: string;
    hook_type: string;
    status: string;
    preview: string;
  }>;
}

interface HooksData {
  groups: HookGroup[];
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const STATUS_PILL: Record<string, { bg: string; text: string; label: string }> = {
  approved: { bg: "#DCFCE7", text: "#15803D", label: "Approved" },
  draft:    { bg: "#FEF3C7", text: "#92400E", label: "Draft" },
  rejected: { bg: "#FEE2E2", text: "#991B1B", label: "Rejected" },
};

function StatusBadge({ count, status }: { count: number; status: string }) {
  if (count === 0) return null;
  const style = STATUS_PILL[status] ?? { bg: "#F5F5F4", text: "#6B7280", label: status };
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {count} {style.label}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-20 rounded bg-[hsl(var(--surface-2))]" />
      ))}
    </div>
  );
}

export function HookLibrary() {
  const session = getSession();
  const [data, setData] = useState<HooksData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    const token = session?.token;
    if (!token) { setError("Not authenticated"); setLoading(false); return; }

    fetch("/api/dashboards/marketing/hooks", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => {
        console.error("[HookLibrary]", e);
        setError("Could not load hook library");
        setLoading(false);
      });
  }, [session?.token]);

  function toggleGroup(theme: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(theme)) next.delete(theme);
      else next.add(theme);
      return next;
    });
  }

  if (loading) return <Skeleton />;
  if (error) return <p className="text-[12px] text-[#DC2626]">{error}</p>;

  const groups = data?.groups ?? [];

  if (groups.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-6 text-center">
        <p className="text-[13px] text-muted-foreground">
          No outreach hooks found. Create hooks in the{" "}
          <a href="#/marketing/hooks" className="text-[#0F766E] hover:underline">
            hooks workspace
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((g) => {
        const isOpen = expanded.has(g.campaign_theme);
        return (
          <div
            key={g.campaign_theme}
            className="rounded-lg border border-border bg-[hsl(var(--surface))] overflow-hidden"
          >
            {/* Group header */}
            <button
              type="button"
              onClick={() => toggleGroup(g.campaign_theme)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[hsl(var(--surface-2))] transition-colors text-left"
              aria-expanded={isOpen}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-[13px] font-semibold text-foreground truncate">
                  {g.campaign_theme}
                </span>
                {/* Count badge */}
                <span
                  className="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                  style={{ backgroundColor: "#0F766E", minWidth: 20 }}
                >
                  {g.count}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <StatusBadge count={g.approved} status="approved" />
                <StatusBadge count={g.draft} status="draft" />
                <StatusBadge count={g.rejected} status="rejected" />
                <span className="text-[11px] text-muted-foreground ml-2">
                  Last fired: {formatDate(g.last_fired)}
                </span>
                {/* Chevron */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  className={`transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                >
                  <path d="M4 6l4 4 4-4" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </button>

            {/* Expanded hook list */}
            {isOpen && (
              <div className="border-t border-border divide-y divide-border">
                {g.hooks.map((h) => {
                  const pill = STATUS_PILL[h.status] ?? { bg: "#F5F5F4", text: "#6B7280", label: h.status };
                  return (
                    <div key={h.id} className="px-4 py-3 flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                          {h.hook_type.replace("_", " ")}
                        </span>
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                          style={{ backgroundColor: pill.bg, color: pill.text }}
                        >
                          {pill.label}
                        </span>
                      </div>
                      {h.preview && (
                        <p className="text-[12px] text-muted-foreground leading-relaxed line-clamp-2">
                          {h.preview}
                          {h.preview.length >= 120 ? "…" : ""}
                        </p>
                      )}
                    </div>
                  );
                })}
                <div className="px-4 py-2">
                  <a
                    href="#/marketing/hooks"
                    className="text-[11px] text-[#0F766E] hover:underline font-medium"
                  >
                    Manage all hooks in workspace
                  </a>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
